import React, { useState } from 'react';
import { Modal, type ModalProps } from '../';

export interface ConfirmModalProps
  extends Omit<ModalProps, 'open' | 'onOk' | 'onCancel' | 'footer' | 'confirmLoading'> {
  /** Confirm callback. Async returns are awaited; the modal closes on resolve and stays open on reject. */
  onConfirm: () => void | Promise<unknown>;
  /** Cancel callback. Fires when the user clicks Cancel, the close X, the mask, or presses Escape. */
  onCancel?: () => void;
  /** Body content of the modal. Note: `children` is the trigger, not the content. */
  content?: React.ReactNode;
  /** Clickable element that opens the modal when clicked. */
  children: React.ReactNode;
}

export const ConfirmModal = ({
  title = 'Confirm Action',
  onConfirm,
  onCancel,
  okText = 'Confirm',
  cancelText = 'Cancel',
  okButtonProps,
  cancelButtonProps,
  content,
  children,
  ...props
}: ConfirmModalProps) => {
  const [visible, setVisible] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleConfirm = async () => {
    setLoading(true);
    try {
      await onConfirm();
      setLoading(false);
      setVisible(false);
    } catch (err) {
      setLoading(false);
      throw err;
    }
  };

  const handleCancel = () => {
    setVisible(false);
    onCancel?.();
  };

  return (
    <>
      <Modal
        {...props}
        title={title}
        open={visible}
        onOk={handleConfirm}
        onCancel={handleCancel}
        okText={okText}
        cancelText={cancelText}
        confirmLoading={loading}
        okButtonProps={{
          'data-testid': 'confirm-modal-confirm-button',
          ...okButtonProps,
          disabled: loading || okButtonProps?.disabled,
        }}
        cancelButtonProps={{
          'data-testid': 'confirm-modal-cancel-button',
          ...cancelButtonProps,
          disabled: loading || cancelButtonProps?.disabled,
        }}
        footer={undefined}
      >
        {content && <div data-testid="confirm-modal-content">{content}</div>}
      </Modal>
      <div onClick={() => setVisible(true)} data-testid="confirm-modal-trigger">
        {children}
      </div>
    </>
  );
};
